/**
 * Date utility functions for consistent date handling across the application
 * Handles timezone-aware parsing and formatting to avoid UTC conversion issues
 */

/**
 * Validates if a date string matches the required format
 */
export const validateDateFormat = (dateStr: string): boolean => {
  if (!dateStr) return false;
  
  // Check if the date matches yyyy-mm-dd hh:mm format
  const displayFormatRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
  if (displayFormatRegex.test(dateStr)) {
    return true;
  }
  
  // Check if it's an ISO date string
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
  if (isoRegex.test(dateStr)) {
    return true;
  }
  
  // If not matching the exact patterns, try to convert to a valid date
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return true;
    }
  } catch (e) {
    // Not a valid date
    console.error("Date conversion failed", e);
  }

  return false;
};

/**
 * Parse a date string without timezone conversion
 * Handles both ISO strings and yyyy-mm-dd hh:mm format
 */
export const parseDateAsLocal = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  
  // If it's an ISO string with Z, treat it as local time
  if (dateStr.includes('T') && dateStr.includes('Z')) {
    // Remove the Z and treat as local time
    const withoutZ = dateStr.replace('Z', '');
    return new Date(withoutZ);
  }
  
  // If it's yyyy-mm-dd hh:mm format, parse manually to avoid timezone issues
  const regex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
  const match = dateStr.match(regex);
  
  if (match) {
    const [, year, month, day, hour, minute] = match;
    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Month is 0-indexed
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10)
    );
  }
  
  // Fallback to standard parsing
  return new Date(dateStr);
};

/**
 * Format a date string for display, ensuring it shows local time
 */
export const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  
  const date = parseDateAsLocal(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Formats a date to an ISO string format for the backend, preserving local time
 * This avoids the timezone conversion issues caused by toISOString()
 */
export const formatDateForBackend = (dateStr: string | number | Date): string => {
  if (!dateStr) return '';
  
  // If it's already a string in our expected format yyyy-mm-dd hh:mm, convert to ISO
  if (typeof dateStr === 'string') {
    const regex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
    const match = dateStr.match(regex);
    
    if (match) {
      // It's in our display format, create local time ISO string
      const [, yearStr, monthStr, dayStr, hourStr, minuteStr] = match;
      
      // Create an ISO-formatted date string preserving local time
      const isoDateString = `${yearStr}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}:00.000Z`;
      return isoDateString;
    }
    
    // If it's already an ISO string, return it
    if (dateStr.includes('T') && dateStr.includes('Z')) {
      return dateStr;
    }
  }
  
  try {
    // Handle Date objects or parse string dates
    let date: Date;
    
    if (dateStr instanceof Date) {
      date = dateStr;
    } else if (typeof dateStr === 'string') {
      // Try to manually parse our expected format first
      const regex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
      const match = dateStr.match(regex);
      
      if (match) {
        // It's in the correct format, extract components
        const [, year, month, day, hour, minute] = match;
        date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1, // Month is 0-indexed
          parseInt(day, 10),
          parseInt(hour, 10),
          parseInt(minute, 10)
        );
      } else {
        // Try standard date parsing
        date = new Date(dateStr);
      }
    } else {
      // Number or other type
      date = new Date(dateStr);
    }
    
    if (!isNaN(date.getTime())) {
      // Return local time in ISO-like format for the backend
      // Using local time components to avoid UTC conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      const localIsoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
      return localIsoString;
    }
  } catch (e) {
    // Invalid date format
  }
  
  // If we can't format it, return the original string
  return String(dateStr);
};

/**
 * Convert Excel serial date to JavaScript Date, then format for backend
 */
export const formatExcelDateForBackend = (excelDate: number): string => {
  // Convert Excel serial date to JavaScript Date
  const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
  const msPerDay = 24 * 60 * 60 * 1000;
  const javascriptDate = new Date(excelEpoch.getTime() + excelDate * msPerDay);
  return formatDateForBackend(javascriptDate);
};

/**
 * Format a Date object for display in DD/MM/YYYY HH:MM format
 * This function was moved from lib/utils.ts for consistency
 * @param date - Date object to format
 * @returns Formatted date string in DD/MM/YYYY HH:MM format
 */
export function formatDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${day}/${month}/${year} ${time}`;
}
